import { APP_CONFIG } from '@/config';

const compact = (value: string) => value.trim().replace(/\s+/g, ' ');

type Match = { offset: number; length: number };
type Suggestion = {
  id: string;
  label: string;
  matches?: Match[];
  address?: { street?: string; locality?: string };
};

export type AddressSuggestion = {
  id: string;
  label: string;
  matches: Match[];
  value: string;
};

const getSuggestions = async (query: string, includedTypes: string, signal?: AbortSignal): Promise<Suggestion[]> => {
  const url = new URL(APP_CONFIG.geoAutocompleteUrl);
  url.search = new URLSearchParams({
    query,
    country: 'DEU',
    locale: 'de-DE',
    included_types: includedTypes,
    limit: '20',
  }).toString();
  const response = await fetch(url.toString(), {
    credentials: 'omit',
    signal,
    headers: { 'X-GAC-Client-Key': APP_CONFIG.geoAutocompleteClientKey },
  });
  if (!response.ok) throw new Error(`Address lookup failed (${response.status})`);
  const payload = await response.json();
  if (!Array.isArray(payload?.suggestions)) throw new Error('Invalid address suggestions');

  return payload.suggestions;
};

export const getCitySuggestions = async (postalCode: string, signal?: AbortSignal): Promise<AddressSuggestion[]> => {
  const suggestions = await getSuggestions(postalCode, 'area', signal);
  // shortcut: GAC omits structured area/street names; parse labels until it supplies them.
  return suggestions.map((suggestion) => ({
    id: suggestion.id,
    label: suggestion.label,
    matches: suggestion.matches ?? [],
    value: compact(suggestion.address?.locality || suggestion.label.replace(/^\d{5}\s+/, '')),
  }));
};

export const getStreetSuggestions = async (
  postalCode: string,
  city: string,
  street: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> => {
  const query = [compact(street), `${postalCode} ${compact(city)}`].filter(Boolean).join(', ');
  const suggestions = await getSuggestions(query, 'street', signal);
  return suggestions.map((suggestion) => ({
    id: suggestion.id,
    label: suggestion.label,
    matches: suggestion.matches ?? [],
    value: compact(suggestion.address?.street || suggestion.label.split(',')[0]),
  }));
};
